// row 112 CoverageGrid (the intake coverage matrix, renamed by DC-42): values × rounds and skills × rounds as a dense
// table on --card-2. Each cell is a 22 px --r-tile square: covered (--t-green with a check), uncovered (--t-red with
// --red-text, when a required row has no covered cell; the row head also carries the red *Uncovered* pill), open (the
// field ground, when the row is covered elsewhere) or n/a (--t-neutral). Heads are uppercase --text-3. A row head with
// `href` is a jump link to the row's place on the page (F row jump); the grid scrolls inside its card with a sticky
// first column (it composes Table, #45, which becomes a focusable named region when it overflows). Each group is its
// own <tbody> headed by a scope="rowgroup" cell, so a cell's row group is announced correctly.

import type { ReactNode } from "react";
import { Icon } from "../core/icons";
import { TextLink } from "../core/Link";
import { Pill } from "../core/Pill";
import { Table, Td, Th, Tr } from "../core/Table";
import { cx } from "../lib/cx";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { G1_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type CoverageCell = "covered" | "open" | "na";

export interface CoverageRound {
  key: string;
  /** The round's name. */
  label: string;
}

export interface CoverageRow {
  key: string;
  /** The value's or skill's name. */
  label: string;
  /** Must be covered before finalization (every value; a required skill). */
  required?: boolean | undefined;
  /** The jump link to the row's place on the page. */
  href?: string | undefined;
  /** Per round key; a missing key is n/a. */
  cells: Readonly<Record<string, CoverageCell>>;
  hs?: Hs | undefined;
}

export interface CoverageGroup {
  /** The group's heading (*ROOTS*, *Skill* …). */
  label: string;
  rows: readonly CoverageRow[];
}

export interface CoverageGridProps {
  rounds: readonly CoverageRound[];
  groups: readonly CoverageGroup[];
  /** The grid's name (the table's aria-label and the region's when it scrolls). */
  label: string;
  /** Head of the first column (default: none drawn). */
  corner?: ReactNode | undefined;
  className?: string | undefined;
}

function uncovered(row: CoverageRow, rounds: readonly CoverageRound[]): boolean {
  return Boolean(row.required) && !rounds.some((round) => row.cells[round.key] === "covered");
}

function Cell({ state, bad, round }: { state: CoverageCell; bad: boolean; round: string }) {
  const kind = state === "covered" ? "ok" : state === "na" ? "na" : bad ? "bad" : "open";
  return (
    <Td align="c" label={round} className="mc-cg-td">
      <span className={cx("mc-cg", `mc-cg-${kind}`)} aria-hidden="true">
        {kind === "ok" ? <Icon name="check" size={14} /> : kind === "bad" ? <Icon name="x" size={14} /> : null}
      </span>
      <VisuallyHidden>{kind === "ok" ? G1_LABELS.covered : kind === "bad" ? G1_LABELS.uncovered : "—"}</VisuallyHidden>
    </Td>
  );
}

export function CoverageGrid({ rounds, groups, label, corner, className }: CoverageGridProps) {
  const head = (
    <Tr>
      <Th>{corner ?? <VisuallyHidden>{label}</VisuallyHidden>}</Th>
      {rounds.map((round) => <Th key={round.key} align="c">{round.label}</Th>)}
    </Tr>
  );
  return (
    <Table
      head={head}
      label={label}
      dense
      stickyFirst
      className={cx("mc-cgrid", className)}
      bodies={groups.map((group) => (
        <tbody key={group.label}>
          <tr className="mc-cg-group"><th scope="rowgroup" colSpan={rounds.length + 1}>{group.label}</th></tr>
          {group.rows.map((row) => {
            const bad = uncovered(row, rounds);
            return (
              <Tr key={row.key} hs={row.hs} className={bad ? "mc-cg-row-bad" : undefined}>
                <Th row>
                  <span className="mc-cg-name">
                    {row.href !== undefined ? <TextLink href={row.href}>{row.label}</TextLink> : row.label}
                    {bad ? <Pill tone="red" dot={false}>{G1_LABELS.uncovered}</Pill> : null}
                  </span>
                </Th>
                {rounds.map((round) => <Cell key={round.key} state={row.cells[round.key] ?? "na"} bad={bad} round={round.label} />)}
              </Tr>
            );
          })}
        </tbody>
      ))}
    />
  );
}
